import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { motion, AnimatePresence, useReducedMotion } from 'framer-motion';
import { generateConfetti, ConfettiParticle, Milestone } from '../utils/animations';

// ============================================================================
// CONFETTI SHAPES
// ============================================================================

interface ShapeProps {
  color: string;
  size: number;
}

const Circle: React.FC<ShapeProps> = ({ color, size }) => (
  <div
    style={{
      width: size,
      height: size,
      backgroundColor: color,
      borderRadius: '50%'
    }}
  />
);

const Square: React.FC<ShapeProps> = ({ color, size }) => (
  <div
    style={{
      width: size,
      height: size,
      backgroundColor: color,
      borderRadius: 2
    }}
  />
);

const Heart: React.FC<ShapeProps> = ({ color, size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
  </svg>
);

const Star: React.FC<ShapeProps> = ({ color, size }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={color}>
    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
  </svg>
);

const ShapeRenderer: React.FC<{ shape: ConfettiParticle['shape']; color: string; size: number }> = ({
  shape,
  color,
  size
}) => {
  switch (shape) {
    case 'heart':
      return <Heart color={color} size={size} />;
    case 'star':
      return <Star color={color} size={size} />;
    case 'square':
      return <Square color={color} size={size} />;
    default:
      return <Circle color={color} size={size} />;
  }
};

// ============================================================================
// CONFETTI PARTICLE COMPONENT
// ============================================================================

interface ConfettiPieceProps {
  particle: ConfettiParticle;
  duration: number;
}

const ConfettiPiece: React.FC<ConfettiPieceProps> = ({ particle, duration }) => {
  const size = 10 * particle.scale;

  return (
    <motion.div
      initial={{
        x: `${particle.x}vw`,
        y: `${particle.y}vh`,
        rotate: particle.rotation,
        scale: 0
      }}
      animate={{
        x: `${particle.x + particle.velocityX * 20}vw`,
        y: '110vh',
        rotate: particle.rotation + particle.rotationSpeed * 50,
        scale: [0, particle.scale, particle.scale, 0]
      }}
      transition={{
        duration: duration,
        ease: [0.25, 0.1, 0.25, 1],
        scale: {
          times: [0, 0.1, 0.8, 1],
          duration: duration
        }
      }}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        pointerEvents: 'none',
        zIndex: 9999
      }}
    >
      <ShapeRenderer shape={particle.shape} color={particle.color} size={size} />
    </motion.div>
  );
};

// ============================================================================
// CONFETTI BURST COMPONENT
// ============================================================================

interface ConfettiBurstProps {
  isActive: boolean;
  particleCount?: number;
  duration?: number;
  onComplete?: () => void;
}

export const ConfettiBurst: React.FC<ConfettiBurstProps> = ({
  isActive,
  particleCount = 50,
  duration = 4,
  onComplete
}) => {
  const [particles, setParticles] = useState<ConfettiParticle[]>([]);
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (isActive && !prefersReducedMotion) {
      setParticles(generateConfetti(particleCount));

      const timer = setTimeout(() => {
        setParticles([]);
        onComplete?.();
      }, duration * 1000);

      return () => clearTimeout(timer);
    } else {
      setParticles([]);
    }
  }, [isActive, particleCount, duration, onComplete, prefersReducedMotion]);

  if (prefersReducedMotion) return null;

  return (
    <AnimatePresence>
      {particles.map(particle => (
        <ConfettiPiece key={particle.id} particle={particle} duration={duration} />
      ))}
    </AnimatePresence>
  );
};

// ============================================================================
// CELEBRATION TOAST COMPONENT
// ============================================================================

interface CelebrationToastProps {
  milestone: Milestone | null;
  onDismiss: () => void;
}

export const CelebrationToast: React.FC<CelebrationToastProps> = ({ milestone, onDismiss }) => {
  const prefersReducedMotion = useReducedMotion();

  useEffect(() => {
    if (milestone) {
      const timer = setTimeout(onDismiss, 5000);
      return () => clearTimeout(timer);
    }
  }, [milestone, onDismiss]);

  if (!milestone) return null;

  const getToastIcon = () => {
    switch (milestone.type) {
      case 'birthday':
        return '🎂';
      case 'round_days':
        return '🌟';
      case 'round_weeks':
        return '✨';
      case 'season_complete':
        return '🌸';
      case 'first_visit':
        return '👋';
      default:
        return '🎉';
    }
  };

  return (
    <AnimatePresence>
      {milestone && (
        <motion.div
          initial={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: 50, scale: 0.9 }}
          animate={prefersReducedMotion ? { opacity: 1 } : { opacity: 1, y: 0, scale: 1 }}
          exit={prefersReducedMotion ? { opacity: 0 } : { opacity: 0, y: -20, scale: 0.9 }}
          transition={{ type: 'spring', stiffness: 300, damping: 20 }}
          className="fixed bottom-8 left-1/2 transform -translate-x-1/2 z-50"
          onClick={onDismiss}
        >
          <div className="bg-white dark:bg-catppuccin-surface0 rounded-2xl shadow-2xl px-6 py-4 flex items-center gap-3 cursor-pointer hover:scale-105 transition-transform border border-gray-100 dark:border-catppuccin-surface1">
            <span className="text-3xl">{getToastIcon()}</span>
            <div>
              <p className="font-bold text-gray-800 dark:text-catppuccin-text">
                Celebration!
              </p>
              <p className="text-gray-600 dark:text-catppuccin-subtext0 text-sm">
                {milestone.message}
              </p>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ============================================================================
// CELEBRATION MANAGER COMPONENT
// ============================================================================

interface CelebrationManagerProps {
  daysLived: number;
  weeksLived: number;
  currentSeason: string;
  birthdate: string;
}

export const CelebrationManager: React.FC<CelebrationManagerProps> = ({
  daysLived,
  weeksLived,
  currentSeason,
  birthdate
}) => {
  const [activeMilestone, setActiveMilestone] = useState<Milestone | null>(null);
  const [showConfetti, setShowConfetti] = useState(false);
  const [hasCheckedFirstVisit, setHasCheckedFirstVisit] = useState(false);

  // Check for first visit
  useEffect(() => {
    if (!hasCheckedFirstVisit && birthdate) {
      const visited = localStorage.getItem('mortalmath_visited');
      if (!visited) {
        localStorage.setItem('mortalmath_visited', 'true');
        setActiveMilestone({
          type: 'first_visit',
          message: 'Welcome to your life journey!'
        });
        setShowConfetti(true);
      }
      setHasCheckedFirstVisit(true);
    }
  }, [birthdate, hasCheckedFirstVisit]);

  // Check for round number milestones
  useEffect(() => {
    if (!birthdate || activeMilestone) return;

    // Round day milestones
    const roundDays = [1000, 5000, 10000, 15000, 20000, 25000, 30000];
    const currentDay = Math.floor(daysLived);

    for (const milestone of roundDays) {
      if (currentDay === milestone) {
        setActiveMilestone({
          type: 'round_days',
          value: milestone,
          message: `${milestone.toLocaleString()} days of life!`
        });
        setShowConfetti(true);
        return;
      }
    }

    // Round week milestones
    const roundWeeks = [500, 1000, 1500, 2000, 2500, 3000];
    const currentWeek = Math.floor(weeksLived);

    for (const milestone of roundWeeks) {
      if (currentWeek === milestone) {
        setActiveMilestone({
          type: 'round_weeks',
          value: milestone,
          message: `${milestone.toLocaleString()} weeks of life!`
        });
        setShowConfetti(true);
        return;
      }
    }
  }, [daysLived, weeksLived, birthdate, activeMilestone]);

  const handleDismiss = useCallback(() => {
    setActiveMilestone(null);
    setShowConfetti(false);
  }, []);

  const handleConfettiComplete = useCallback(() => {
    setShowConfetti(false);
  }, []);

  // Memoize the confetti component to prevent unnecessary re-renders
  const confettiBurst = useMemo(() => (
    <ConfettiBurst
      isActive={showConfetti}
      particleCount={60}
      duration={4}
      onComplete={handleConfettiComplete}
    />
  ), [showConfetti, handleConfettiComplete]);

  return (
    <>
      {confettiBurst}
      <CelebrationToast milestone={activeMilestone} onDismiss={handleDismiss} />
    </>
  );
};

// ============================================================================
// MANUAL CELEBRATION TRIGGER HOOK
// ============================================================================

export const useCelebration = () => {
  const [isActive, setIsActive] = useState(false);

  const trigger = useCallback(() => {
    setIsActive(true);
    setTimeout(() => setIsActive(false), 4000);
  }, []);

  return { isActive, trigger };
};

export default CelebrationManager;
